/**
 * Shared utility functions for profile statistics, XP, and level calculations
 */

/**
 * Calculate level based on total XP
 * @param totalXp - Total experience points
 * @returns Current level based on XP
 */
export function calculateLevelFromXp(totalXp: number): number {
  // Formula: Level = 1 + floor(sqrt(totalXp / 10))
  return 1 + Math.floor(Math.sqrt(totalXp / 10));
}

/**
 * Calculate XP needed for the next level
 * @param level - Current level
 * @returns XP needed for the next level
 */
export function calculateXpForNextLevel(level: number): number {
  // Formula: currentLevel * 100
  return level * 100;
}

/**
 * Calculates the exact amount of XP required for a specific level.
 * The formula is (level - 1)² * 10
 * 
 * Examples:
 * - Level 1: 0 XP
 * - Level 2: 10 XP
 * - Level 3: 40 XP
 * - Level 4: 90 XP
 * - Level 5: 160 XP
 * 
 * @param level - The level to calculate XP for
 * @returns The exact amount of XP required to be at this level
 */
export function getRequiredXpForLevel(level: number): number {
  if (level <= 1) return 0;
  return ((level - 1) ** 2) * 10;
}

/**
 * Calculate XP rewards based on game type, win/loss status, and player level
 * 
 * @param gameType - Type of game (ranked, quick, tournament, ai, etc.)
 * @param isWinner - Whether the player won the game
 * @param playerLevel - Current level of the player
 * @param gameScore - Score achieved in the game
 * @returns The XP amount to award
 */
export function calculateGameXp(gameType: string, isWinner: boolean, playerLevel: number, gameScore: number): number {
  // Base XP values by game type
  const baseXpValues = {
    ranked: 30,
    tournament: 20,
    quick: 10,
    ai: 5,
    custom: 8,
    private: 8,
    public: 8,
    friends: 8
  };

  // Default to custom if game type is not recognized
  const baseXp = baseXpValues[gameType as keyof typeof baseXpValues] || baseXpValues.custom;
  
  // Win bonus - winners get 2x XP
  const winMultiplier = isWinner ? 2.0 : 0.5;
  
  // Level scaling - higher levels get slightly diminishing returns to prevent easy leveling
  // Formula creates a curve that gradually decreases as level increases
  const levelScalingFactor = Math.max(0.5, 1 - (playerLevel - 1) * 0.01);
  
  // Score bonus - small bonus based on score achieved (1 XP per 5 points)
  const scoreBonus = Math.floor(gameScore / 5);
  
  // Calculate final XP
  const totalXp = Math.round((baseXp * winMultiplier * levelScalingFactor) + scoreBonus);
  
  return Math.max(1, totalXp); // Always award at least 1 XP
}

/**
 * Format win rate for consistent display across the app
 * @param winRate - Raw win rate value
 * @returns Formatted win rate string with no decimal places
 */
export function formatWinRate(winRate: number | string | null): string {
  if (winRate === null || winRate === undefined) return "0";
  
  const numericWinRate = typeof winRate === 'string' ? parseFloat(winRate) : winRate;
  return Math.round(numericWinRate).toString();
}